/**
 * Variant-change enhancement for the product template.
 *
 * The server render is the source of truth. Option inputs resolve to a
 * variant locally (variants JSON is embedded by the section); the section is
 * then re-fetched via the Section Rendering API and every [data-rerender]
 * region — claim band, price, sku, quantity, pickup — is swapped in. Any
 * fetch failure falls back to full navigation, so the shopper never sees a
 * stale claim.
 */
class WaypointProduct extends HTMLElement {
  connectedCallback() {
    this.variants = this.readJson('[data-variants]') ?? [];
    this.variantMedia = this.readJson('[data-variant-media]') ?? {};
    this.filterVariantMedia = this.dataset.filterVariantMedia === 'true';
    this.abortController = null;

    for (const input of this.querySelectorAll('[data-option-input]')) {
      input.addEventListener('change', () => this.onOptionChange());
    }

    for (const button of this.querySelectorAll('[data-media-button]')) {
      button.addEventListener('click', () => this.activateMedia(button.dataset.mediaButton));
    }

    this.setupRecipient();
  }

  readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  selectedOptions() {
    const options = [];
    for (const input of this.querySelectorAll('[data-option-input]:checked')) {
      options[Number(input.dataset.optionIndex)] = input.value;
    }
    return options;
  }

  currentVariant() {
    const selected = this.selectedOptions();
    return this.variants.find((candidate) =>
      candidate.options.every((value, index) => value === selected[index]),
    );
  }

  onOptionChange() {
    const variant = this.currentVariant();

    const buyButtons = this.querySelectorAll('[data-buy-button]');
    if (!variant) {
      for (const buyButton of buyButtons) {
        buyButton.disabled = true;
        buyButton.textContent = this.dataset.unavailableText || buyButton.textContent;
      }
      return;
    }

    const idInput = this.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;

    this.showVariantMedia(variant);
    this.rerender(variant.id);
  }

  showVariantMedia(variant) {
    if (this.filterVariantMedia) this.filterMedia(variant.id);

    const mediaId = variant.featured_media?.id ?? this.variantMedia[String(variant.id)];
    if (!mediaId) return;
    this.activateMedia(String(mediaId), variant.title);
  }

  filterMedia(variantId) {
    const variantToken = `|${variantId}|`;
    for (const button of this.querySelectorAll('[data-media-button]')) {
      const variantIds = button.dataset.variantIds ?? '';
      button.hidden = variantIds !== '' && !variantIds.includes(variantToken);
    }
  }

  activateMedia(mediaId, mediaLabel = '') {
    const gallery = this.querySelector('[data-media-gallery]');
    const item = gallery?.querySelector(`[data-media-id="${mediaId}"]`);
    if (item) {
      const image = item.querySelector('img');
      if (image) {
        image.loading = 'eager';
        if (mediaLabel && this.dataset.productTitle) {
          image.alt = `${this.dataset.productTitle} — ${mediaLabel}`;
        }
      }
      for (const mediaItem of gallery.children) {
        const active = mediaItem === item;
        mediaItem.classList.toggle('product__media-item--active', active);
        mediaItem.hidden = !active;
      }
      for (const button of this.querySelectorAll('[data-media-button]')) {
        button.setAttribute('aria-current', String(button.dataset.mediaButton === String(mediaId)));
      }
    }
  }

  async rerender(variantId) {
    const variantUrl = `${this.dataset.url}?variant=${encodeURIComponent(variantId)}`;

    this.abortController?.abort();
    this.abortController = new AbortController();

    let markup;
    try {
      const response = await fetch(
        `${variantUrl}&section_id=${encodeURIComponent(this.dataset.sectionId)}`,
        { signal: this.abortController.signal },
      );
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
      markup = await response.text();
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.href = variantUrl;
      return;
    }

    const doc = new DOMParser().parseFromString(markup, 'text/html');

    for (const target of this.querySelectorAll('[data-rerender]')) {
      const source = doc.querySelector(`[data-rerender="${target.dataset.rerender}"]`);
      if (source) target.replaceChildren(...source.childNodes);
    }

    const buyButtons = this.querySelectorAll('[data-buy-button]');
    const nextBuyButton = doc.querySelector('[data-buy-button]');
    if (nextBuyButton) {
      for (const buyButton of buyButtons) {
        buyButton.disabled = nextBuyButton.disabled;
        buyButton.textContent = nextBuyButton.textContent;
      }
    }

    window.history.replaceState({}, '', variantUrl);
  }

  setupRecipient() {
    const recipient = this.querySelector('[data-recipient]');
    if (!recipient) return;
    recipient.setAttribute('data-js', '');

    const checkbox = recipient.querySelector('[data-recipient-checkbox]');
    const offset = recipient.querySelector('[data-recipient-offset]');
    const sync = () => {
      const on = checkbox?.checked ?? false;
      recipient.classList.toggle('recipient--open', on);
      if (offset) {
        offset.disabled = !on;
        offset.value = String(new Date().getTimezoneOffset());
      }
    };
    checkbox?.addEventListener('change', sync);
    sync();
  }
}

customElements.define('waypoint-product', WaypointProduct);
